import { EChartOption } from 'echarts';
import * as echarts from 'echarts/lib/echarts';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import moment from 'moment';
import { timelineDataSelector } from '@store/timeline/data/selectors';
import getTextWidth from '@utils/DOM/getTextWidth';

const bg = ['#ccc', '#ddd'];

const renderItem: any = (params, api) => {
  const index = get(params, ['dataIndex'], 0);
  const startPoint = api.value(0);
  const endPoint = api.value(1);
  const backgroundColor = bg[index % 2];
  const start = api.coord([startPoint]);
  const end = api.coord([endPoint]);
  const width = end[0] - start[0];
  const height = 25;

  const backgroundRect = echarts.graphic.clipRectByRect(
    {
      x: start[0],
      y: 15,
      width: width > 2 ? width - 2 : width,
      height,
    },
    {
      x: params.coordSys.x,
      y: 15,
      width: params.coordSys.width,
      height,
    },
  );
  let text = api.value(2);
  let textWidth = getTextWidth(text) + 15;

  while (textWidth > width && text !== '') {
    const substring = text.toString().substring(0, text.length - 5); // CE-9085 fix: text is not always string
    text = substring === '' ? '' : substring + '...';
    textWidth = getTextWidth(text) + 15;
  }

  const style = api.style({
    text,
    textFill: '#333',
  });
  return {
    textConfig: {
      position: 'insideLeft',
    },
    type: 'rect',
    style: {...style, fill: backgroundColor},
    shape: backgroundRect,
    styleEmphasis: {
      fill: backgroundColor,
      opacity: 1,
    },
  };
};

const useStateOption = (eventKey: string, dateRange: number[]): EChartOption => {
  const timelineData = useSelector(timelineDataSelector);
  const min = dateRange[0];
  const max = dateRange[dateRange.length - 1];
  const diff = moment(max).diff(min, 'm');
  const stateTimelineData = (get(timelineData, [eventKey], []) as Array<{ startTimeMs: number; endTimeMs: number; value: string }>);
  const stateData = stateTimelineData.reduce((
    result: Array<[number, number, string]>,
    { startTimeMs, endTimeMs, value },
  ) => {
    const start = Math.max(min, startTimeMs);
    const end = Math.min(max, endTimeMs);
    if (start > end) {
      return result;
    }
    result.push([
      start,
      end,
      value,
    ]);
    return result;
  }, []);

  return {
    tooltip: {},
    grid: {
      show: true,
      left: 30,
      right: 95,
      top: 15,
      bottom: 20,
      borderWidth: 0,
      backgroundColor: '#F6F6F6',
      shadowColor: 'rgba(0, 0, 0, 0.1)',
      shadowBlur: 0.5,
      shadowOffsetY: 1,
    },
    xAxis: [{
      type: 'time',
      left: 20,
      right: 95,
      bottom: 20,
      height: 20,
      min,
      max,
      data: dateRange,
      axisLine: { show: false },
      splitLine: { show: false },
      axisLabel: {
        showMinLabel: true,
        showMaxLabel: true,
        formatter: value => moment(value).format('HH:mm'),
      },
      axisTick: { show: false },
      splitNumber: Math.min(14, diff),
    }],
    yAxis: [{
      axisLine: { show: false },
      splitLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        show: false,
        showMinLabel: false,
        showMaxLabel: false,
      },
    }],
    series: [
      {
        type: 'custom',
        renderItem,
        data: stateData,
        tooltip: {
          formatter: params => {
            const [startTime, endTime, errorMsg] = get(
              params,
              ['value'],
              ['N/A', 1, ''],
            );
            return `
              <div>
                <strong>${moment(startTime).format('MMM DD, YYYY HH:mm:ss')}</strong>
                <span> to </span>
                <strong>${moment(endTime).format('MMM DD, YYYY HH:mm:ss')}</strong>
              </div>
              <div>${errorMsg}</div>
            `;
          },
        },
      },
    ],
  };
};

export default useStateOption;
